import { Box, Flex } from '@chakra-ui/layout';
import { Alert, AlertDescription, AlertIcon } from '@chakra-ui/react';
import React, { useCallback, useMemo } from 'react';

import { useCreditFacilityOrThrow } from '../../../../contexts/CreditFacilitiesProvider';
import {
  mapEmptyAttributeValueToAttribute,
  mapGqlAttributeToAttributeInstances,
} from '../../../common-frontend/components/AttributeInput/utils';
import {
  GetCustomerProvidedVariablesAndValuesDocumentGQLType,
  useCreateOrUpdateCustomerProvidedValueMutation,
  useGetCustomerProvidedVariablesAndValuesQuery,
} from '../../requests/requests.gql.generated';
import {
  AttributeWithEffectiveDateEditor,
  UpdateParams,
} from './AttributeWithEffectiveDateEditor';

export const CustomerProvidedVariablesContainer = ({
  creditFacilityId,
}: {
  creditFacilityId: string;
}) => {
  const creditFacility = useCreditFacilityOrThrow(creditFacilityId);

  const {
    data: attributesData,
    loading,
    error,
  } = useGetCustomerProvidedVariablesAndValuesQuery({
    variables: {
      creditFacilityId,
    },
  });

  const [
    createOrUpdateAttributeValue,
  ] = useCreateOrUpdateCustomerProvidedValueMutation({
    refetchQueries: [GetCustomerProvidedVariablesAndValuesDocumentGQLType],
  });

  const onUpdateAttribute = useCallback(
    async (updatedAttribute: UpdateParams) => {
      try {
        await createOrUpdateAttributeValue({
          variables: {
            attributeId: updatedAttribute.attributeId,
            creditFacilityId: creditFacility.id,
            effectiveFromReportingDate: updatedAttribute.effectiveFromDate,
            value: updatedAttribute.value,
          },
        });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.log(err);
      }
    },
    [createOrUpdateAttributeValue, creditFacility.id],
  );

  // generate attribute instances and attribute templates from the GQL response
  // on FE a single type (Attribute) is used to represent both
  const attributes = useMemo(() => {
    const rawAttributes = attributesData?.attributes || [];

    // some attributes would have values (instances) associated with them
    // pick the instances first.
    const instances = rawAttributes
      .filter((attr) => attr.attributeValues.length !== 0)
      .map((attr) => mapGqlAttributeToAttributeInstances(attr))
      .flat()
      .sort((a, b) => {
        return a.displayName.localeCompare(b.displayName);
      });

    // some attributes that do not have instances. we need to surface those as well
    const templates = rawAttributes
      .filter((attr) => attr.attributeValues.length === 0)
      .map((attr) => mapEmptyAttributeValueToAttribute(attr));

    return [...instances, ...templates];
  }, [attributesData?.attributes]);

  if (loading) {
    return <div>Loading...</div>;
  }
  if (error) {
    return <div>Error: {error.message}</div>;
  }

  return (
    <Box width="100%" height="100%">
      {attributes.length === 0 && (
        <Alert status="info">
          <AlertIcon />
          <AlertDescription>
            No variables are set for this credit facility. Please contact your
            Finley representative to add variables.
          </AlertDescription>
        </Alert>
      )}
      <Flex direction="column" gap="1rem" mt="2.5rem">
        <AttributeWithEffectiveDateEditor
          attributes={attributes}
          updateAttribute={onUpdateAttribute}
        />
      </Flex>
    </Box>
  );
};
